import { SplitPaneDrawer } from '@/Layout/Drawer/SplitPaneDrawer';
import { useSetPayee } from '@/components/ApPayee/context';
import { useOCRCancelModal } from '@/components/DataReflection/CancelModal/useOCRCancelModal';
import { Invoice } from '@/components/ReportForm/ApReportFormInputInvoiceFileField/type';
import { useSyncSelectedApPayeeIsBank } from '@/components/ReportForm/InstantPayeeSwitcher/hooks/useSyncSelectedApPayeeIsBank';
import { useTpmDataFromBusinessPersonData } from '@/context/BusinessPersonData';
import { useOcrContext } from '@/features/InvoiceReport/Edit/components/Context/OcrContext';
import { Preview } from '@/features/InvoiceReport/Edit/components/Drawer/OcrReflectionDrawer/Components/Preview/Preview';
import { useChangeInvoiceKind } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/hooks/useChangeInvoiceKind';
import { OcrReflectionApplicationForm } from '@/features/InvoiceReport/Edit/components/OcrReflectionApplicationForm';
import { OcrReflectionDetailsForm } from '@/features/InvoiceReport/Edit/components/OcrReflectionDetailsForm';
import { OcrReflectionReferencedApplicationForm } from '@/features/InvoiceReport/Edit/components/OcrReflectionReferencedApplicationForm';
import { InferenceAlerts } from '@/features/InvoiceReport/Edit/components/ReportFormInputs/ReportFormInputs';
import { type useFillPaymentRequestDetails } from '@/features/InvoiceReport/Edit/components/hooks/useFillPaymentRequestDetails';
import { type useFillPaymentRequestSummary } from '@/features/InvoiceReport/Edit/components/hooks/useFillPaymentRequestSummary';
import { OcrInferenceResult } from '@/features/InvoiceReport/Edit/components/type';
import {
  transformApPayeeForContext,
  transformForApplicationForm,
  transformForPaymentDetailsForm,
} from '@/features/InvoiceReport/Edit/components/utils/ocrDataTransform';
import { PaymentRequestForm } from '@/features/InvoiceReport/Edit/type';
import { useTranslation } from '@/i18n';
import { ButtonV2, useToastAPI } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import { Control } from 'react-hook-form';
import styles from './OcrReflectionDrawer.module.scss';
import { useApplicationFormSelectionManager } from './hooks/useApplicationFormSelectionManager';

const cx = classnames.bind(styles);

export type Props = {
  setOcrInferenceResult: (result: OcrInferenceResult | null) => void;
  isVisible: boolean;
  invoice: Invoice;
  ocrInferenceResult: OcrInferenceResult | null;
  bookDateInputId?: string;
  dueDateInputId?: string;
  payeeInputId?: string;
  willOverwriteTransactions?: boolean;
  canDisplayDrExcise?: boolean;
  invoiceRegistrationNumberKey?: string;
  isHiddenInvoiceRegistrationNumber?: boolean;
  isActiveInstantPayee?: boolean;
  isEnabledInstantPayee?: boolean;
  draftInstantPayeeBankName?: string;
  draftInstantPayeeBankBranchName?: string;
  fillPaymentRequestSummary: ReturnType<typeof useFillPaymentRequestSummary>;
  fillPaymentRequestDetails: ReturnType<typeof useFillPaymentRequestDetails>;
  control: Control<PaymentRequestForm>;
  isDutyFree: boolean;
  inferenceAlerts?: InferenceAlerts;
};

export const OcrReflectionDrawer: FC<Props> = memo(
  ({
    isVisible,
    invoice,
    invoiceRegistrationNumberKey,
    isHiddenInvoiceRegistrationNumber,
    ocrInferenceResult,
    bookDateInputId,
    dueDateInputId,
    payeeInputId,
    isActiveInstantPayee,
    isEnabledInstantPayee,
    draftInstantPayeeBankName,
    draftInstantPayeeBankBranchName,
    willOverwriteTransactions,
    canDisplayDrExcise,
    setOcrInferenceResult,
    fillPaymentRequestSummary,
    fillPaymentRequestDetails,
    control,
    isDutyFree,
    inferenceAlerts,
  }) => {
    const { t } = useTranslation();
    const { confirm, ConfirmModal } = useOCRCancelModal();
    const notify = useToastAPI();
    const setApPayeeInContext = useSetPayee();
    const syncIsBank = useSyncSelectedApPayeeIsBank();
    const { aiProgressType, markOcrSubmitted } = useOcrContext();

    const [shouldReflectDetails, setShouldReflectDetails] = useState(
      !willOverwriteTransactions
    );

    const isOcrReadPayee = useMemo(() => {
      return ocrInferenceResult?.apPayee?.id !== undefined;
    }, [ocrInferenceResult]);

    const {
      isInstantPayeeOpened,
      setIsInstantPayeeOpened,
      isVisibleInstantPayee,
      toggleAllChecked,
      allChecked,
      indeterminate,
      selectionState,
      updateSelection,
      initializeSelection,
      getMappedFieldValues,
    } = useApplicationFormSelectionManager({
      isOcrReadPayee,
      isEnabledInstantPayee,
      isActiveInstantPayee,
      invoiceRegistrationNumberKey,
    });

    const tpmData = useTpmDataFromBusinessPersonData();
    const { changeInvoiceKindByInvoiceRegistrationNumber } =
      useChangeInvoiceKind(control, isDutyFree);

    const updateInvoiceKindForAllTransactions = useCallback(async () => {
      changeInvoiceKindByInvoiceRegistrationNumber(tpmData);
    }, [changeInvoiceKindByInvoiceRegistrationNumber, tpmData]);

    const handleCancelClick = async () => {
      const result = await confirm();
      if (result) {
        setOcrInferenceResult(null);
        initializeSelection();
      }
    };

    const handleConfirmClick = async () => {
      if (ocrInferenceResult) {
        // TODO(james): Improve type-safety here. Currently:
        // * applicationForm: Record<keyof SelectionState, string | Dayjs | null>
        // * mappedFieldValues: Record<string, unknown>
        const applicationForm = transformForApplicationForm(ocrInferenceResult);
        const mappedFieldValues = getMappedFieldValues(applicationForm, {
          bookDateInputId,
          dueDateInputId,
          payeeInputId,
        });

        // TODO: `isInstantPayeeOpened` looks like a boolean.
        // Refactor: make `checkIsInstantPayeeOpened()` function, then
        // call it and put the result in booean `isInstantPayeeOpened`
        if (isInstantPayeeOpened === 'instant') {
          setApPayeeInContext(undefined);
          syncIsBank(undefined);
        } else if (
          selectionState.payeeSelected && // Only set payee if payee is selected
          applicationForm.payeeSelected !== null &&
          applicationForm.payeeSelected !== undefined
        ) {
          const apPayee = transformApPayeeForContext(
            ocrInferenceResult.apPayee
          );

          setApPayeeInContext(apPayee);
          syncIsBank(apPayee);
        }

        // TODO(james): Consider refactoring the data structure completely.
        // The data starts as an API response, and gets transformed several times
        // before it finally gets sent to `fillPaymentRequestSummary`. A lot of that
        // may be unnecessary
        fillPaymentRequestSummary(mappedFieldValues, {
          invoiceRegistrationNumberKey,
          selectionState,
        });
      }

      if (ocrInferenceResult?.invoiceTransactions && shouldReflectDetails) {
        const paymentRequestDetails = transformForPaymentDetailsForm(
          ocrInferenceResult?.invoiceTransactions
        );
        fillPaymentRequestDetails(paymentRequestDetails);
      }

      await updateInvoiceKindForAllTransactions();

      markOcrSubmitted();
      inferenceAlerts?.dueDate?.onBlur();
      inferenceAlerts?.payee?.onBlur();
      inferenceAlerts?.invoiceRegistrationNumber?.onBlur();

      notify.success({
        title: t('ai_ocr_reflection_success'),
        duration: 3,
      });
      setOcrInferenceResult(null);
      initializeSelection();
    };

    const RightPaneContent = <Preview invoice={invoice} />;
    const LeftPaneContent = (
      <>
        {aiProgressType === 'ai_suggest' &&
          ocrInferenceResult &&
          ocrInferenceResult?.reference_report && (
            <OcrReflectionReferencedApplicationForm
              id={ocrInferenceResult?.reference_report?.id ?? undefined}
              number={ocrInferenceResult?.reference_report?.number ?? undefined}
              title={ocrInferenceResult?.reference_report?.title ?? undefined}
            />
          )}
        <OcrReflectionApplicationForm
          ocrInferenceResult={ocrInferenceResult}
          invoiceRegistrationNumberKey={invoiceRegistrationNumberKey}
          isHiddenInvoiceRegistrationNumber={isHiddenInvoiceRegistrationNumber}
          draftInstantPayeeBankName={draftInstantPayeeBankName}
          draftInstantPayeeBankBranchName={draftInstantPayeeBankBranchName}
          isInstantPayeeOpened={isInstantPayeeOpened}
          setIsInstantPayeeOpened={setIsInstantPayeeOpened}
          isVisibleInstantPayee={isVisibleInstantPayee}
          toggleAllChecked={toggleAllChecked}
          allChecked={allChecked}
          indeterminate={indeterminate}
          selectionState={selectionState}
          updateSelection={updateSelection}
        />
        <OcrReflectionDetailsForm
          isChecked={shouldReflectDetails}
          setIsChecked={setShouldReflectDetails}
          ocrInferenceResult={ocrInferenceResult}
          willOverwriteTransactions={willOverwriteTransactions}
          canDisplayDrExcise={canDisplayDrExcise}
        />
      </>
    );

    return (
      <>
        <SplitPaneDrawer
          width={1200}
          title={
            aiProgressType === 'ai_suggest'
              ? t('payment_request_edit_ocr.drawer.title_ai_suggestion')
              : t('payment_request_edit_ocr.drawer.title')
          }
          onClose={handleCancelClick}
          isVisible={isVisible}
          leftPaneContent={LeftPaneContent}
          rightPaneContent={RightPaneContent}
          isMask={isVisible}
          footerContent={
            <div className={cx(styles['drawer-content-footer'])}>
              <ButtonV2 isSecondary onClick={handleCancelClick} size='sm'>
                {t('payment_request_edit_ocr.drawer.cancel_button')}
              </ButtonV2>
              <ButtonV2 onClick={handleConfirmClick} size='sm'>
                {t('payment_request_edit_ocr.drawer.confirm_button')}
              </ButtonV2>
            </div>
          }
          leftPaneClassName={cx(styles['body-left-pane'])}
          rightPaneClassName={cx(styles['body-right-pane'])}
        />
        <ConfirmModal />
      </>
    );
  }
);
OcrReflectionDrawer.displayName = 'OcrReflectionDrawer';
